import { inject, Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { AlertButton, AlertController } from '@ionic/angular';
import { UtilsServices } from '../../components/utils-services';
import dayjs from 'dayjs';
import { TranslateService } from '@ngx-translate/core';
import { Browser } from '@capacitor/browser';
import { Preferences } from '@capacitor/preferences';
import { getConfigOrElse } from '../config/config.service';
import { OidcStorageService } from '../../../oidc/storage/oidc-storage.service';
import { LoggerFactory } from '../logger/logger-factory.service';
import { LoggerWrapper } from '../logger/logger-wrapper.service';
import { AdfsOidcSecurityService } from '../../../oidc/adfs-oidc-security.service';
import { firstValueFrom } from 'rxjs';

const LAST_DATE_DISPLAY_ALERT_EXPIRATION_PASSWORD_KEY = 'last_date_display_alert_expiration_password';

@Injectable({ providedIn: 'root' })
export class CheckPasswordService {
  private readonly utilsServices: UtilsServices = inject(UtilsServices);
  private readonly alertController: AlertController = inject(AlertController);
  private readonly translateService: TranslateService = inject(TranslateService);
  private readonly oidcStorageService: OidcStorageService = inject(OidcStorageService);
  private readonly loggerFactory: LoggerFactory = inject(LoggerFactory);
  private readonly adfsOidcSecurityService: AdfsOidcSecurityService = inject(AdfsOidcSecurityService);
  private logger: LoggerWrapper;

  constructor() {
    this.logger = this.loggerFactory.getInstance(this, true);
  }

  public async checkPasswordExpiration() {
    try {
      await firstValueFrom(this.adfsOidcSecurityService.forceRefreshSession('normal'));
    } catch (error) {
      this.logger.error(error, "Une erreur s'est produite lors du forceRefreshSession du checkPasswordExpiration");
    }
    const userData = await this.oidcStorageService.getUserDataFromStore('normal');
    const userEmail = userData['email'];
    const pwdLastSetLdapTimestampString = userData['custom:pwdlastset'];
    this.logger.info(`${userEmail} : pwdLastSetLdapTimestampString = `, pwdLastSetLdapTimestampString);

    if (pwdLastSetLdapTimestampString) {
      const pwdLastSetLdapTimestamp = BigInt(pwdLastSetLdapTimestampString);
      this.logger.info(`${userEmail} : pwdLastSetLdapTimestamp = `, pwdLastSetLdapTimestamp.toString());

      const lastSetPassword = dayjs(this.utilsServices.ldapTimestampToDate(pwdLastSetLdapTimestamp));
      this.logger.info(`${userEmail} : lastSetPassword = `, lastSetPassword.toISOString());

      const daysUntilPasswordExpiryAlert = Number(getConfigOrElse('DAYS_UNTIL_PASSWORD_EXPIRY_ALERT', '15'));
      this.logger.info(`${userEmail} : daysUntilPasswordExpiryAlert = `, daysUntilPasswordExpiryAlert);

      const durationPasswordAdfs = Number(getConfigOrElse('ADFS_PASSWORD_DURATION', '90'));
      this.logger.info(`${userEmail} : durationPasswordAdfs = `, durationPasswordAdfs);

      const remindStartDate = lastSetPassword.add(durationPasswordAdfs - daysUntilPasswordExpiryAlert, 'days');
      this.logger.info(`${userEmail} : remindStartDate = `, remindStartDate.toISOString());

      const today = dayjs();
      this.logger.info(`${userEmail} : today = `, today.toISOString());

      if (await this.shouldPresentAlertExpirationPassword(today, remindStartDate)) {
        await this.saveDateInCache(today);
        this.presentAlertExpirationPassword(today, lastSetPassword, durationPasswordAdfs);
      }
    }
  }

  private async shouldPresentAlertExpirationPassword(today: dayjs.Dayjs, remindStartDate: dayjs.Dayjs) {
    const lastDateDisplayAlertExpirationPassword = (
      await Preferences.get({ key: LAST_DATE_DISPLAY_ALERT_EXPIRATION_PASSWORD_KEY })
    ).value;
    return (
      this.isNotAlreadyDisplayToday(lastDateDisplayAlertExpirationPassword, today) &&
      this.isSameOrAfterRemindStartDate(today, remindStartDate)
    );
  }

  private isSameOrAfterRemindStartDate(today: dayjs.Dayjs, remindStartDate: dayjs.Dayjs) {
    return today.isSame(remindStartDate) || today.isAfter(remindStartDate);
  }

  private isNotAlreadyDisplayToday(value: string, today: dayjs.Dayjs) {
    return !value || dayjs(value).isBefore(today, 'days');
  }

  private async saveDateInCache(today: dayjs.Dayjs) {
    await Preferences.set({
      key: LAST_DATE_DISPLAY_ALERT_EXPIRATION_PASSWORD_KEY,
      value: today.toISOString(),
    });
  }

  private presentAlertExpirationPassword(
    today: dayjs.Dayjs,
    lastSetPassword: dayjs.Dayjs,
    durationPasswordAdfs: number
  ) {
    const expirationPasswordDate = lastSetPassword.add(durationPasswordAdfs, 'days');
    const daysLeft = expirationPasswordDate.diff(today, 'days');

    this.alertController
      .create({
        header: this.translateService.instant('AUTHENTICATION.PASSWORD_EXPIRATION.ALERT_MESSAGE', {
          nbJourRestant: daysLeft,
        }),
        buttons: this.createAlertButtons(),
        cssClass: 'alert-modify-expired-password',
      })
      .then((alert) => alert.present());
  }

  private createAlertButtons() {
    let laterButton: AlertButton = {
      text: this.translateService.instant('AUTHENTICATION.PASSWORD_EXPIRATION.LATER_BUTTON'),
    };
    let modifyButton: AlertButton = {
      text: this.translateService.instant('AUTHENTICATION.PASSWORD_EXPIRATION.MODIFY_BUTTON'),
      cssClass: 'modify-expired-password-button',
      handler: () => Browser.open({ url: environment.redirectUrlModifyAdfsPassword }),
    };
    return [laterButton, modifyButton];
  }
}
